import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, MessageCircle, Menu, X, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { STORE, waLink, telLink } from "@/lib/store";

const NAV = [
    { to: "/", label: "Home", end: true },
    { to: "/#categories", label: "Categories" },
    { to: "/#about", label: "About" },
    { to: "/contact", label: "Contact" },
];

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => { setOpen(false); }, [location.pathname]);

    return (
        <header
            data-testid="site-header"
            className={`sticky top-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-white/85 backdrop-blur-xl border-b border-border shadow-sm"
                    : "bg-white border-b border-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="h-16 md:h-20 flex items-center justify-between">
                    <Link to="/" data-testid="header-logo" className="flex items-center gap-3 group">
                        <img
                            src="/brand/ths-logo.jpg"
                            alt="The Hardware Store logo"
                            className="w-10 h-10 md:w-11 md:h-11 rounded-md object-cover border border-black/5"
                        />
                        <span className="font-display font-extrabold text-lg md:text-xl text-charcoal tracking-tight leading-none">
                            The Hardware <span className="text-safety">Store</span>
                        </span>
                    </Link>

                    <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
                        {NAV.map((n) => (
                            <HeaderLink key={n.label} to={n.to} end={n.end} label={n.label} />
                        ))}
                    </nav>

                    <div className="hidden md:flex items-center gap-2">
                        <a
                            href="https://prismprocure.ai"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="header-login-btn"
                        >
                            <Button
                                className="rounded-full h-10 px-5 bg-safety hover:bg-safety-hover text-white font-semibold btn-shine shadow-sm shadow-safety/30"
                            >
                                Login
                            </Button>
                        </a>
                        <a href={telLink()} data-testid="header-call-btn">
                            <Button
                                variant="outline"
                                className="rounded-full h-10 border-charcoal/15 hover:border-charcoal hover:bg-charcoal hover:text-white transition-colors"
                            >
                                <Phone className="w-4 h-4 mr-2" /> Call
                            </Button>
                        </a>
                        <a href={waLink()} target="_blank" rel="noreferrer" data-testid="header-whatsapp-btn">
                            <Button className="rounded-full h-10 bg-safety hover:bg-safety-hover text-white btn-shine">
                                <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
                            </Button>
                        </a>
                    </div>

                    <button
                        onClick={() => setOpen((v) => !v)}
                        className="lg:hidden inline-flex w-10 h-10 items-center justify-center rounded-md border border-border"
                        aria-label="Toggle menu"
                        data-testid="mobile-menu-toggle"
                    >
                        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="lg:hidden border-t border-border bg-white" data-testid="mobile-menu">
                    <nav className="px-4 py-4 flex flex-col gap-1">
                        {NAV.map((n) => (
                            <a
                                key={n.label}
                                href={n.to}
                                className="px-3 py-3 rounded-md hover:bg-secondary text-charcoal font-medium"
                                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                            >
                                {n.label}
                            </a>
                        ))}
                        <div className="grid grid-cols-2 gap-2 mt-2">
                            <a href={telLink()} data-testid="mobile-call-btn">
                                <Button variant="outline" className="w-full rounded-full h-11">
                                    <Phone className="w-4 h-4 mr-2" /> Call
                                </Button>
                            </a>
                            <a href={waLink()} target="_blank" rel="noreferrer" data-testid="mobile-whatsapp-btn">
                                <Button className="w-full rounded-full h-11 bg-safety hover:bg-safety-hover text-white">
                                    <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
                                </Button>
                            </a>
                        </div>
                        <a
                            href="https://prismprocure.ai"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="mobile-login-btn"
                            className="mt-2"
                        >
                            <Button className="w-full rounded-full h-11 bg-safety hover:bg-safety-hover text-white font-semibold">
                                Login
                            </Button>
                        </a>
                    </nav>
                </div>
            )}
        </header>
    );
}

function HeaderLink({ to, end, label }) {
    const isHash = to.includes("#");
    if (isHash) {
        return (
            <a
                href={to}
                data-testid={`nav-${label.toLowerCase()}`}
                className="px-4 py-2 rounded-full text-sm font-medium text-charcoal/80 hover:text-charcoal hover:bg-secondary transition-colors"
            >
                {label}
            </a>
        );
    }
    return (
        <NavLink
            to={to}
            end={end}
            data-testid={`nav-${label.toLowerCase()}`}
            className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    isActive ? "bg-charcoal text-white" : "text-charcoal/80 hover:text-charcoal hover:bg-secondary"
                }`
            }
        >
            {label}
        </NavLink>
    );
}
